import { useEffect, useState } from "react";

declare global {
	interface Window {
		isFlexApp?: boolean;
		flexAppPlatform?: string;
		flexAppVersion?: string;
		flexAppInsets?: {
			top: number;
			bottom: number;
			left: number;
			right: number;
		};
	}
}

function getCookie(name: string): string | null {
	const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
	return match ? match[2] : null;
}

const FLEX_FLAG_KEY = "amber:is-flex-app";

function readPersistedFlag(): boolean {
	try {
		return window.localStorage.getItem(FLEX_FLAG_KEY) === "true";
	} catch {
		return false;
	}
}

function persistFlag(): void {
	try {
		window.localStorage.setItem(FLEX_FLAG_KEY, "true");
	} catch {
		// Non-fatal: detection just falls back to the live signals each time.
	}
}

/**
 * Detects if running inside the Amber Flex native app shell.
 * Reads from three sources (whichever is truthy first):
 * 1. window.isFlexApp — injected by native before React renders
 * 2. is_flex_app cookie — seeded by native before first request
 * 3. a flag persisted the first time either of the above was seen
 *
 * (3) exists because neither live signal is reliable at every moment. On iOS
 * the shell injects window.isFlexApp with `injectedJavaScript`, which runs
 * AFTER the document has loaded, and the cookie is host-only — so a
 * full-document navigation (the redirect out of a dead route, for instance)
 * can land on a page where both read false for the first few hundred ms.
 * Storage here is per-origin and per-app, so a browser never picks this up.
 */
export const isFlexApp = () => {
	if (typeof window === "undefined") return false;
	if (Boolean(window.isFlexApp) || getCookie("is_flex_app") === "true") {
		persistFlag();
		return true;
	}
	return readPersistedFlag();
};

const useIsFlexApp = () => {
	const [flexApp, setFlexApp] = useState(isFlexApp);

	useEffect(() => {
		if (flexApp) return;

		// Keep looking for a short window, for the late-injection case above:
		// a flag that arrives after mount still has to flip the UI.
		let cancelled = false;
		const check = () => {
			if (cancelled || !isFlexApp()) return false;
			setFlexApp(true);
			return true;
		};

		if (check()) return;

		const interval = window.setInterval(() => {
			if (check()) window.clearInterval(interval);
		}, 150);
		const stop = window.setTimeout(() => window.clearInterval(interval), 3000);
		window.addEventListener("load", check);

		return () => {
			cancelled = true;
			window.clearInterval(interval);
			window.clearTimeout(stop);
			window.removeEventListener("load", check);
		};
	}, [flexApp]);

	return flexApp;
};

export const flexAppPlatform = (): string =>
	typeof window !== "undefined" ? window.flexAppPlatform || "" : "";

export const flexAppVersion = (): string =>
	typeof window !== "undefined" ? window.flexAppVersion || "" : "";

export const flexAppInsets = () =>
	typeof window !== "undefined" ? window.flexAppInsets || null : null;

export default useIsFlexApp;
